import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardTitle, Col, DropdownMenu, DropdownToggle, Row, UncontrolledDropdown } from 'reactstrap';
import { UserId } from '@business/entities/account';
import { FileResponse, FolderId, FolderResponse } from '@business/entities/file';
import { FormTypes } from '@business/entities/form';
import { OrgId } from '@business/entities/organization';
import { FileService, RoleService } from '@business/services';
import { useBloc } from '@core/utils/bloc';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { Session } from '@modules/auth/session';
import { openDocumentEditorModal } from '@modules/document/DocumentEditorModal';
import { openAddEditFormModal } from '@modules/form/AddEditFormModal';
import { openFormShareModal } from '@modules/form/FormShareModal';
import { opemFormViewModal } from '@modules/form/FormViewModal';
import { tap } from 'rxjs/operators';
import { openDownloadFolderModal } from '.';
import { CBreadcrumb, CBreadcrumbItem } from '../Breadcrumb';
import { openGenericInputModal } from '../GenericInputModal';
import { openSelectUsersModal } from '../SelectUsersModal';
import { openAddFiles } from './AddFilesModal';
import { FileItem } from './FileItem';
import { openFileOrgModal } from './MoveFileOrgModal';
import { openMoveFiles } from './MoveFilesModal';
import { SearchList } from './SearchList';
import { openOrgFileShareModal } from './OrgFileShareModal';
import { HasPermission } from '@core/components/HasPermission';
import { OrgFilesBloc } from './OrgFilesBloc';
import { openAddPresentationModal } from '@modules/form/AddPresentationModal';
import { openPresentationSummaryModal } from '@modules/presentation/PresentationSummaryModal';
import { useCompany } from '@core/utils/hooks/misc';
import { AppConfig } from '@core/utils/config';

export interface OrgFilesProps {
  card?: boolean;
  orgId: OrgId;
  type: FormTypes;
  defaultFolderId?: FolderId;
  moduleId?: string | null;
  moduleType?: string;
}

export const OrgFiles = memo((props: OrgFilesProps) => {
  const { card = false, orgId, defaultFolderId, moduleId, moduleType } = props;
  const bloc = useBloc(OrgFilesBloc, orgId);
  const items = useObservable(bloc.items$, []);
  const searchItems = useObservable(bloc.searchItems$, []);
  const breadcrumbs = useNonNilObservable(bloc.breadcrumb$);
  const user = useObservable(Session.user$);
  const showForm = useMemo(() => (user ? RoleService.isStaff(user) : false), [user]);
  const [search, setSearch] = useState('');
  const [folderId, setFolderId] = useState<string>('0');
  const [defaultFolderRendered, setDefaultFolderRendered] = useState(false);
  const company = useCompany();

  const onSearch = useCallback(
    (search: string) => {
      bloc.search(search, folderId).subscribe();
    },
    [bloc, folderId],
  );

  const onDebouncedSearch = useMemo(() => R.debounce(onSearch, 250), [onSearch]);

  useEffect(() => {
    if (items.length > 0 && defaultFolderId && !defaultFolderRendered) {
      const item = items.find(i => i.id === defaultFolderId);
      if (item) {
        setFolderId(item.id);
        setDefaultFolderRendered(true);
      }
    }
  }, [items, defaultFolderId, defaultFolderRendered]);

  useEffect(() => {
    if (!R.isEmpty(search)) {
      onDebouncedSearch(search);
    }
  }, [search, onDebouncedSearch]);

  const breadcrumbItems: CBreadcrumbItem[] = useMemo(
    () => breadcrumbs.map(b => ({ title: b.name, onClick: () => setFolderId(b.id) })),
    [breadcrumbs],
  );

  const onAddFolder = useCallback(() => {
    openGenericInputModal({
      title: 'Create Folder',
      label: 'Folder name',
      button: 'Create',
      onSubmit: name => bloc.createFolder(orgId, name, folderId),
    });
  }, [bloc, orgId, folderId]);

  const onRename = useCallback(
    (item: FileResponse | FolderResponse) => {
      if (item.type === 'folder') {
        openGenericInputModal({
          title: 'Rename Folder',
          label: 'Folder name',
          button: 'Update',
          initialValue: item.name,
          onSubmit: name => bloc.renameFolder(item.id, name),
        });
      } else {
        openGenericInputModal({
          title: 'Rename File',
          label: 'Filename',
          button: 'Update',
          initialValue: item.name,
          onSubmit: name => bloc.renameFile(item.id, name),
        });
      }
    },
    [bloc],
  );

  const onShare = useCallback((item: FileResponse | FolderResponse) => {
    if (item.type !== 'folder' && item.fileType === 'form') {
      openFormShareModal({
        formId: item.id,
      });
    }
  }, []);

  const onClick = useCallback(
    (item: FileResponse | FolderResponse) => {
      if (item.type === 'folder') {
        setSearch('');
        setFolderId(item.id);
      } else {
        if (item.fileType === 'file') {
          window.open(item.url);
        } else if (item.fileType === 'document') {
          openDocumentEditorModal({ documentId: item.id.toString(), title: item.name, breadcrumbs });
        } else if (item.fileType === 'form') {
          opemFormViewModal({
            title: item.name,
            fileForm: item.form!,
          });
        } else if (item.fileType === 'presentation') {
          const appId = AppConfig.get().appId;
          window.open(`/presentation/${item.objectId}/preview/${appId}/${company.id}`);
        }
      }
    },
    [breadcrumbs, company.id],
  );

  const onDelete = useCallback(
    (item: FileResponse | FolderResponse) => {
      if (item.type === 'folder') {
        bloc.deleteFolder(item.id).subscribe();
      } else {
        bloc.deleteFile(item.id).subscribe();
      }
    },
    [bloc],
  );

  const onMove = useCallback(
    (item: FileResponse | FolderResponse) => {
      openMoveFiles({ orgId, id: item.id, type: item.type, onMove: () => bloc.getItems(orgId, folderId).subscribe() });
    },
    [bloc, folderId, orgId],
  );

  const onChangeOrg = useCallback((item: FileResponse | FolderResponse) => {
    openFileOrgModal({
      item: item,
    });
  }, []);

  const onVisibility = useCallback(
    (item: FileResponse | FolderResponse) => {
      if (item.type === 'folder') {
        bloc.changeFolderVisibility(item.id).subscribe();
      } else {
        bloc.changeFileVisibility(item.id).subscribe();
      }
    },
    [bloc],
  );

  const onShareUsers = useCallback(
    (item: FileResponse | FolderResponse) =>
      openSelectUsersModal({
        onlyStaff: false,
        title: 'Users',
        button: 'Update',
        placeholder: 'Users',
        userIds: item.users,
        required: false,
        onSelect: (userIds: UserId[]) => bloc.itemShareUsers(item.id, item.type, userIds),
      }),
    [bloc],
  );

  const onAddDocment = useCallback(() => {
    openGenericInputModal({
      title: 'Create Document',
      label: 'Document name',
      button: 'Create',
      onSubmit: name =>
        bloc.createDocument(orgId, name, folderId).pipe(
          tap(file => {
            openDocumentEditorModal({ documentId: file.id.toString(), title: file.name, breadcrumbs });
          }),
        ),
    });
  }, [bloc, orgId, folderId, breadcrumbs]);

  const onPermission = useCallback(
    (item: FileResponse | FolderResponse) =>
      openOrgFileShareModal({
        item: item,
      }),
    [],
  );

  useEffect(() => {
    bloc.getItems(orgId, folderId).subscribe();
  }, [bloc, folderId, orgId]);

  const onFormSelect = useCallback((item: FileResponse) => {
    opemFormViewModal({ title: item.name, fileForm: item.form! });
  }, []);

  const onBreadcrumb = useCallback((id: FolderId) => {
    setSearch('');
    setFolderId(id);
  }, []);

  const onDetail = useCallback(
    (item: FileResponse | FolderResponse) => {
      const iItem = item as FileResponse;
      if (iItem.objectId) {
        openPresentationSummaryModal({
          presentationId: iItem.objectId,
          orgId: orgId,
        });
      }
    },
    [orgId],
  );
  const canAddPresentation = useMemo(
    () => props.moduleType && FileService.canAddPresentation(props.moduleType as FormTypes, user),
    [props.moduleType, user],
  );

  const content = (
    <Fragment>
      <div className="d-flex justify-content-between align-items-center">
        <CardTitle className="mb-0"></CardTitle>
        <div className="d-flex align-items-center">
          <div className="search-box mr-2">
            <div className="position-relative">
              <input
                value={search}
                type="text"
                className="form-control bg-light border-light rounded"
                placeholder="Search..."
                onChange={event => setSearch(event.target.value)}
              />
              <i className="bx bx-search-alt search-icon"></i>
            </div>
          </div>
          <HasPermission module={'organizations'} moduleKey={'files'} action="add">
            <UncontrolledDropdown className="d-flex align-items-center">
              <DropdownToggle tag="a" className="text-muted hover-pointer">
                <i className="fa fa-plus-circle font-size-20 text-primary"></i>
              </DropdownToggle>
              <DropdownMenu right>
                <a className="dropdown-item" onClick={onAddFolder}>
                  Add Folder
                </a>
                <a
                  className="dropdown-item"
                  onClick={() => openAddFiles({ orgId, folderId, onClose: () => bloc.getItems(orgId, folderId).subscribe() })}
                >
                  Add Files
                </a>
                <a className="dropdown-item" onClick={onAddDocment}>
                  Add Document
                </a>
                {showForm && (
                  <a className="dropdown-item" onClick={() => openAddEditFormModal({ type: props.type, onSelect: onFormSelect, orgId, folderId })}>
                    Add Form
                  </a>
                )}
                {canAddPresentation && (
                  <a
                    className="dropdown-item"
                    onClick={() => openAddPresentationModal({ type: props.type, onSelect: onFormSelect, orgId, folderId, moduleId, moduleType })}
                  >
                    Add Presentation
                  </a>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          </HasPermission>
        </div>
      </div>
      <hr className="mb-2" />
      <Row>
        {!search && (
          <Col sm={12}>
            <CBreadcrumb items={breadcrumbItems} />
          </Col>
        )}
        {!!search && (
          <Col sm={12}>
            <SearchList items={searchItems} onClick={onClick} onBreadcrumb={onBreadcrumb} />
          </Col>
        )}
        {!search &&
          items.map((item, index) => (
            <FileItem
              user={user}
              key={index}
              item={item}
              onShare={() => onShare(item)}
              onPermission={() => onPermission(item)}
              onClick={() => onClick(item)}
              onRename={() => onRename(item)}
              onDelete={() => onDelete(item)}
              onDownload={() => item.type == 'folder' && openDownloadFolderModal({ folder: item, bloc })}
              onMove={() => onMove(item)}
              onChangeOrg={() => onChangeOrg(item)}
              onVisibility={() => onVisibility(item)}
              onShareUsers={() => onShareUsers(item)}
              onStatusChange={(id, status) => bloc.changeFolderStatus(id, status).subscribe()}
              onDetail={() => onDetail(item)}
            />
          ))}
      </Row>
    </Fragment>
  );

  if (card) {
    return (
      <Card>
        <CardBody>
          {content}
          <div style={{ marginBottom: 100 }}></div>
        </CardBody>
      </Card>
    );
  }
  return content;
});
